import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { useNewToast } from 'hooks/newToast';
import { appWarnings } from 'utils/appWarnings';

const RegColheitaSimplificadaContext = createContext({});

const success = { type: 'success', autoClose: 3000 };

export function RegColheitaSimplificadaProvider({ children }) {
  const { showToast } = useNewToast();

  const [regColheitaSimplificada, setRegColheitaSimplificada] = useState([]);
  const [regColheitaSimplificadaLoading, setLoading] = useState(false);
  const [regColheitaItem, setRegColheitaItem] = useState({});
  const [regColheitaItemLoading, setItemLoading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const getRegColheitaSimplificada = useCallback(
    async (registroId) => {
      try {
        setLoading(true);
        const response = await api
          .regAtividade()
          .getRegColheitaSimplificada(registroId);
        setRegColheitaSimplificada(response);
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  const getRegColheitaItem = useCallback(
    async (registroId, itemId) => {
      try {
        setItemLoading(true);
        const response = await api
          .regAtividade()
          .getRegColheitaItem(registroId, itemId);
        setRegColheitaItem(response);
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setItemLoading(false);
      }
    },
    [showToast],
  );

  //
  const insertRegColheitaSimplificada = useCallback(
    async (registroId, data, removeOthers = false) => {
      try {
        setItemLoading(true);

        const formattedData = data?.map((item) => {
          const numCarga = item?.numCarga?.value ?? item?.numCarga;

          const payload = {
            colhido: Number(item?.quantidade),
          };

          // include numCarga when available
          if (numCarga !== undefined && numCarga !== null && numCarga !== '') {
            payload.numCarga = Number(numCarga);
          }

          //
          if (item?.id) {
            payload.id = item.id;
          }

          return payload;
        });

        await api.regAtividade().insertRegColheitaSimplificada(registroId, {
          colheitas: formattedData,
          removeOthers,
        });

        await getRegColheitaSimplificada(registroId);
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setItemLoading(false);
      }
    },
    [showToast, getRegColheitaSimplificada],
  );

  const editSingleItem = useCallback(
    async (registroId, itemId, newData) => {
      try {
        setItemLoading(true);

        const numCargaSingle = newData?.numCarga?.value ?? newData?.numCarga;

        const payload = {
          colheitas: [
            {
              id: itemId,
              colhido: Number(newData.colhido),
              tara: Number(newData.tara || 0),
              ...(numCargaSingle !== undefined &&
              numCargaSingle !== null &&
              numCargaSingle !== ''
                ? { numCarga: Number(numCargaSingle) }
                : {}),
            },
          ],
          removeOthers: false, // Garante que outros itens não sejam deletados
        };

        await api
          .regAtividade()
          .insertRegColheitaSimplificada(registroId, payload);

        await getRegColheitaSimplificada(registroId);
        showToast({
          ...success,
          message: 'Item editado com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setItemLoading(false);
      }
    },
    [showToast, getRegColheitaSimplificada],
  );

  const addNewItem = useCallback(
    async (registroId, newItemData) => {
      try {
        setItemLoading(true);

        const numCargaNew =
          newItemData?.numCarga?.value ?? newItemData?.numCarga;

        const payload = {
          colheitas: [
            {
              colhido: Number(newItemData.colhido),
              tara: Number(newItemData.tara || 0),
              ...(numCargaNew !== undefined &&
              numCargaNew !== null &&
              numCargaNew !== ''
                ? { numCarga: Number(numCargaNew) }
                : {}),
            },
          ],
          removeOthers: false,
        };

        await api
          .regAtividade()
          .insertRegColheitaSimplificada(registroId, payload);

        await getRegColheitaSimplificada(registroId);
        showToast({
          ...success,
          message: 'Item adicionado com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setItemLoading(false);
      }
    },
    [showToast, getRegColheitaSimplificada],
  );

  const syncAllItems = useCallback(
    async (registroId, allItems) => {
      try {
        setItemLoading(true);

        const formattedData = allItems?.map((item) => {
          const numCarga = item?.numCarga?.value ?? item?.numCarga;

          const payload = {
            colhido: Number(item?.quantidade),
            tara: Number(item?.tara || 0),
          };

          if (numCarga !== undefined && numCarga !== null && numCarga !== '') {
            payload.numCarga = Number(numCarga);
          }

          if (item?.id) {
            payload.id = item.id;
          }

          return payload;
        });

        const payload = {
          colheitas: formattedData,
          removeOthers: true,
        };

        await api
          .regAtividade()
          .insertRegColheitaSimplificada(registroId, payload);

        await getRegColheitaSimplificada(registroId);
        showToast({
          ...success,
          message: 'Lista sincronizada com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setItemLoading(false);
      }
    },
    [showToast, getRegColheitaSimplificada],
  );

  const removeRegColheitaItem = useCallback(
    async (registroId, itemId) => {
      try {
        setIsRemoving(true);
        await api
          .regAtividade()
          .removeRegColheitaSimplificada(registroId, itemId);
        showToast({
          ...success,
          message: 'Item removido com sucesso!',
        });
        await getRegColheitaSimplificada(registroId);
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setIsRemoving(false);
      }
    },
    [showToast, getRegColheitaSimplificada],
  );

  const removeAllRegColheitaSimplificada = useCallback(
    async (registroId) => {
      try {
        setIsRemoving(true);
        await api.regAtividade().removeAllRegColheitaSimplificada(registroId);
        showToast({
          ...success,
          message: 'Colheita Manual excluida com sucesso!',
        });
        setRegColheitaSimplificada([]);
      } catch (err) {
        showToast(appWarnings.Danger(err));
      } finally {
        setIsRemoving(false);
      }
    },
    [showToast],
  );

  const contextValue = useMemo(
    () => ({
      regColheitaSimplificada,
      regColheitaSimplificadaLoading,
      regColheitaItem,
      regColheitaItemLoading,
      isRemoving,
      getRegColheitaSimplificada,
      getRegColheitaItem,
      insertRegColheitaSimplificada,
      editSingleItem,
      addNewItem,
      syncAllItems,
      removeRegColheitaItem,
      removeAllRegColheitaSimplificada,
      setRegColheitaSimplificada,
      setLoading,
    }),
    [
      regColheitaSimplificada,
      regColheitaSimplificadaLoading,
      regColheitaItem,
      regColheitaItemLoading,
      isRemoving,
      getRegColheitaSimplificada,
      getRegColheitaItem,
      insertRegColheitaSimplificada,
      editSingleItem,
      addNewItem,
      syncAllItems,
      removeRegColheitaItem,
      removeAllRegColheitaSimplificada,
    ],
  );

  return (
    <RegColheitaSimplificadaContext.Provider value={contextValue}>
      {children}
    </RegColheitaSimplificadaContext.Provider>
  );
}

export function useRegColheitaSimplificada() {
  const context = useContext(RegColheitaSimplificadaContext);

  if (!context) {
    throw new Error(
      'useRegColheitaSimplificada must be used within a RegColheitaSimplificadaProvider',
    );
  }

  return context;
}
